import { useEffect, useState, useCallback } from 'react'; // Removed React import
import { Link } from 'react-router-dom';
import Helmet from 'react-helmet';
import { useToasts } from 'react-toast-notifications';

// API
import InstanceApi from '../../../api/instance/InstanceApi';
import TagApi, { TagToSave, TagToDelete, TagToUpdate } from '../../../api/TagApi';

// Types
import InstanceTarget from '../../../types/instance/InstanceTarget';
import TagsType from '../../../types/Tags';

// Components
import Breadcrumb from '../../../container/breadcrumb';
import HeaderActions from '../../../container/header_actions';
import TagList from './TagList'; // Will be created next

// Helper to define column structure
export interface ClientTagColumnDefinition {
  name: string;
  isNewlyAdded?: boolean; // To track columns added in UI before saving
}

// Helper for data structure passed to TagList
export interface InstanceDisplayRow {
  instanceId: number;
  instanceName: string;
  [key: string]: any; // For dynamic tag columns
  _originalTags: TagsType[]; // To store original tag objects for an instance
}

interface TagsProps {
  isAdmin: boolean;
  loginRequired: boolean; // Assuming similar props to other admin pages
  toggleMenu: Function;
  applyPeriod: Function; // Assuming similar props
}

function Tags(props: TagsProps) {
  const [loading, setLoading] = useState<boolean>(true);
  const { addToast } = useToasts();
  
  // State for what's displayed and potentially edited in the UI
  const [displayData, setDisplayData] = useState<InstanceDisplayRow[]>([]);
  const [columnDefs, setColumnDefs] = useState<ClientTagColumnDefinition[]>([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false); // New state for unsaved changes
  
  // To track original data for diffing on save
  const [originalFetchedTags, setOriginalFetchedTags] = useState<TagsType[]>([]);

  // processFetchedData can be a regular function as it's only called within fetchData with fresh data
  const processFreshData = (freshInstances: InstanceTarget[], freshTags: TagsType[]): { newColumnDefs: ClientTagColumnDefinition[], newDisplayData: InstanceDisplayRow[] } => {
    const uniqueTagNames = Array.from(new Set(freshTags.map(tag => tag.tagname))); // Use freshTags
    const newColumnDefs: ClientTagColumnDefinition[] = uniqueTagNames.map(name => ({ name }));

    const newDisplayData = freshInstances.map(instance => { // Use freshInstances
      const instanceRow: InstanceDisplayRow = {
        instanceId: instance.id,
        instanceName: instance.name || `Instance ${instance.id}`,
        _originalTags: freshTags.filter(tag => tag.datasourceid === instance.id), // Use freshTags
      };
      newColumnDefs.forEach(colDef => {
        const tag = instanceRow._originalTags.find(t => t.tagname === colDef.name);
        instanceRow[colDef.name] = tag ? tag.tagvalue : null;
      });
      return instanceRow;
    });
    return { newColumnDefs, newDisplayData };
  }; // Removed erroneous trailing , []

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [fetchedInstances, fetchedTags] = await Promise.all([
        InstanceApi.getInstances(),
        TagApi.getAllRawTags(),
      ]);
      setOriginalFetchedTags(JSON.parse(JSON.stringify(fetchedTags))); // Deep copy for comparison

      // Use the fresh data directly for processing
      const { newColumnDefs, newDisplayData } = processFreshData(fetchedInstances, fetchedTags);
      setColumnDefs(newColumnDefs);
      setDisplayData(newDisplayData);

    } catch (error) {
      console.error("Failed to fetch instances or tags", error);
      // Handle error (e.g., show error message)
    } finally {
      setLoading(false);
    }
  }, []); // processFreshData is stable, so fetchData's deps can be empty or include other stable refs if needed

  useEffect(() => {
    fetchData();
  }, [fetchData]); // fetchData is memoized with useCallback

  const handleSaveChanges = async () => {
    setLoading(true);
    const tagsToDelete: TagToDelete[] = [];
    const tagsToSave: TagToSave[] = [];
    const tagsToUpdate: TagToUpdate[] = [];

    // Create a map of current tag values for efficient lookup
    const currentTagMap = new Map<string, string | null>();
    displayData.forEach(row => {
      columnDefs.forEach(col => {
        const key = `${row.instanceId}-${col.name}`;
        currentTagMap.set(key, row[col.name]);
      });
    });

    // First, identify tags that need to be deleted
    originalFetchedTags.forEach(originalTag => {
      const key = `${originalTag.datasourceid}-${originalTag.tagname}`;
      const currentValue = currentTagMap.get(key);
      
      // Tag should be deleted if:
      // 1. Current value is null/empty and original was not null/empty
      // 2. OR if the tag no longer exists in the current display data
      if ((currentValue === null || currentValue === '') && 
          originalTag.tagvalue !== null && originalTag.tagvalue !== '') {
        tagsToDelete.push({ id: originalTag.id! });
      }
    });
    
    // Then, identify tags that need to be saved (new or updated)
    displayData.forEach(row => {
      columnDefs.forEach(col => {
        const tagName = col.name;
        const currentValue = row[tagName];

        // Only save if the value is not null/empty
        if (currentValue !== null && currentValue !== undefined && currentValue !== '') {
          const originalInstanceTags = originalFetchedTags.filter(t => t.datasourceid === row.instanceId);
          const originalTag = originalInstanceTags.find(t => t.tagname === tagName);
          
          if (originalTag) {
            // Existing tag - update if value is different
            if (originalTag.tagvalue !== currentValue) {
              tagsToUpdate.push({
                id: originalTag.id!,
                datasourceid: row.instanceId,
                tagname: tagName,
                tagvalue: String(currentValue),
              });
            }
          } else {
            // New tag - create
            tagsToSave.push({
              datasourceid: row.instanceId,
              tagname: tagName,
              tagvalue: String(currentValue),
            });
          }
        }
      });
    });
    
    try {
      // Execute delete operations first
      if (tagsToDelete.length > 0) {
        await TagApi.deleteTagAssignments(tagsToDelete);
      }
      
      // Then execute update operations for existing tags
      if (tagsToUpdate.length > 0) {
        await TagApi.updateTagAssignments(tagsToUpdate);
      }
      
      // Finally execute save operations for new tags
      if (tagsToSave.length > 0) {
        await TagApi.saveTagAssignments(tagsToSave);
      }
      
      // Refresh data from server to get the latest state
      await fetchData();
      setHasUnsavedChanges(false);
      addToast('Tags saved successfully', { appearance: 'success', autoDismiss: true });
    } catch (error) {
      console.error('Failed to save changes', error);
      addToast('Failed to save tags', { appearance: 'error', autoDismiss: true });
    } finally {
      setLoading(false);
    }
  };

  const getUniqueTagValuesForColumn = useCallback((tagName: string): string[] => {
    const values = new Set<string>();
    displayData.forEach(row => {
      if (row[tagName] !== null && row[tagName] !== undefined) {
        values.add(String(row[tagName]));
      }
    });
    return Array.from(values).sort();
  }, [displayData]);


  // Callbacks for TagList to update state in Tags.tsx
  const handleCellEdit = (instanceId: number, tagName: string, newValue: string | null) => { // Allow null for cleared cells
    setDisplayData(prevData =>
      prevData.map(row =>
        row.instanceId === instanceId ? { ...row, [tagName]: newValue } : row
      )
    );
    setHasUnsavedChanges(true); // Mark changes as unsaved
  };

  const handleNewColumn = (newColumnName: string) => {
    if (newColumnName && !columnDefs.find(col => col.name === newColumnName)) {
      setColumnDefs(prevCols => [...prevCols, { name: newColumnName, isNewlyAdded: true }]);
      // Add this new column to all rows in displayData with null initial value
      setDisplayData(prevData => prevData.map(row => ({...row, [newColumnName]: null })));
      setHasUnsavedChanges(true); // Mark changes as unsaved
    }
  };

  return (
    <div id="content">
      <Helmet>
        <title>Instance Tags - Settings - DBmarlin</title>
        <meta name="description" content="Manage instance tags" />
      </Helmet>

      <div id="top-header-wrapper" className="row row-cols-lg-3 row-cols-sm-1 row-cols-md-2">
        <Breadcrumb heading="Instance Tags">
          <Link to="/admin">Settings</Link>
          <span>Instance Tags</span>
        </Breadcrumb>
        <HeaderActions
            toggleMenu={props.toggleMenu} 
            refresh={fetchData}
        />
        {/* Save Changes button has been moved to TagList component's card header */}
      </div>

      <div className="row row-cols-1">
        <div className="col">
          {/* TagList will be rendered here once created */}
           <TagList
            data={displayData}
            columns={columnDefs}
            loadingState={loading}
            onCellEdit={handleCellEdit} 
            onNewColumn={handleNewColumn}
            getUniqueTagValuesForColumn={getUniqueTagValuesForColumn}
            handleSaveChanges={handleSaveChanges} // Pass the save function
            isSaving={loading} // Pass loading state as isSaving
            hasUnsavedChanges={hasUnsavedChanges} // Pass unsaved changes state
            isAdmin={props.isAdmin} // Pass admin status
          />
        </div>
      </div>
    </div>
  );
}

export default Tags;
